/* global FormData */
import React, { useEffect, useState } from 'react'
import {
  additionalFldValues,
  filterByTier,
  filterFlagFields, getTextByLang
} from '../ExternalFields'
import { useStore } from '../../StateProvider'
import { useTranslation } from 'react-i18next'
import { find, propEq, compose, isEmpty, equals, propOr, isNil } from 'ramda'
import { Button, Message } from '../../ui'
import LoginLink from '../pageParts/LoginLink/LoginLink'
import { useRedirectIn10Sec } from '../utils/hooks'
import {
  addAccount,
  fetchQuestionnaireData,
  questionnaireCheck, tryLater,
  verifyAccounts
} from '../api'
import { TierForm } from '../TierForm/TierForm'
import { Page } from '../../ui/Page/Page'
import styles from './style.css'
import { QuestionnaireFailMessage } from '../CreateAccount/QuestionnaireFailMessage/QuestionnaireFailMessage'
import { QuestionnaireTryLaterMessage } from '../CreateAccount/CreateAccount'
import { getQuestionnaireData } from '../utils/utils'
import { pushKYC } from '../utils/googleTag'

export const VerifiedMessage = () => {
  const { t } = useTranslation()
  return (
    <Message title={t('verifyId.verified')}>
      <LoginLink />
    </Message>
  )
}

export const SuccessMessage = ({ shouldRedirect }) => {
  const { t } = useTranslation()

  useRedirectIn10Sec(shouldRedirect)

  return (
    <Message title={t('verifyId.sendedForVerify')} type='clock'>
      {t('verifyId.sendedForVerifyText')}
    </Message>
  )
}

export const RetryError = ({ error, handleTryAgain }) => {
  const { t } = useTranslation()
  return (
    <Message title={propOr(t('verifyId.error'), 'message', error)} type='failed'>
      <Button onClick={handleTryAgain}>{t('verifyId.tryAgain')}</Button>
    </Message>
  )
}

const STATUSES = {
  JOINT_ADD_ACCOUNT_ERROR: 'JOINT_ADD_ACCOUNT_ERROR',
  QUESTIONNAIRE_FAIL: 'QUESTIONNAIRE_FAIL',
  QUESTIONNAIRE_TRY_LATER: 'QUESTIONNAIRE_TRY_LATER'
}

const Tier2Register = () => {
  const [{
    whitelabel: { customTexts, extFields, questionnaireTier },
    user: { questionnairePassed, isJointAccount, docCheckPassed },
    form: { accId },
    form,
    user
  }, dispatch] = useStore()
  const fldsDescriptionList = filterByTier(2)(extFields)
  const { t } = useTranslation()
  const questionnaireEnabled = questionnaireTier === 2

  const [sent, setSent] = useState(false)
  const [sendError, setSendError] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState('')

  const tier2FormCaption = getTextByLang(
    'tier2FormCaption',
    customTexts,
    t('verifyForm.caption')
  )
  const { i18n } = useTranslation()

  useEffect(() => {
    /**
     * @param {{ string: SelectItem[]}} data
     */
    const fetchQuestionnaireDataCallback = (data) => {
      dispatch({ type: 'setData', data, name: 'questionnaire' })
    }
    if (i18n.language && questionnaireEnabled) {
      fetchQuestionnaireData().then(fetchQuestionnaireDataCallback)
    }
  }, [i18n.language, questionnaireEnabled])

  const handleTryAgain = () => {
    setSendError({})
    setIsLoading(false)
    if (isJointAccount && !docCheckPassed && status !== STATUSES.JOINT_ADD_ACCOUNT_ERROR) {
      dispatch({ type: 'setAccId', accId: 0 })
    }
  }

  const getPrimaryExternalFldValues = () => {
    return additionalFldValues(form[accId])(fldsDescriptionList)
  }

  const getFormData = (isJointAcc) => {
    const {
      idScan, idBackSideScan, idType, faceImage
    } = form?.[accId]
    let jointAccountIdObj = {}
    if (isJointAcc) {
      const jointAccountId = user.jointAccounts?.[accId - 1]?.jointAccountId
      jointAccountIdObj = jointAccountId ? { jointAccountId } : {}
    }
    const data = {
      idType,
      ...additionalFldValues(form[accId])(fldsDescriptionList),
      ...jointAccountIdObj
    }
    setIsLoading(true)

    const formData = new FormData()
    formData.append('applicantData', JSON.stringify(data))
    formData.append('idScan', idScan)
    if (isJointAcc) {
      formData.append('joint', isJointAcc)
    }
    if (idBackSideScan) {
      formData.append('idBackSideScan', idBackSideScan)
    }
    if (faceImage) {
      formData.append('faceImage', faceImage)
    }
    return formData
  }

  const onSubmitWithQuestionnaire = () => {
    if (questionnaireEnabled && questionnaireTier === 2 && !questionnairePassed) {
      setIsLoading(true)
      const externalFldValues = getPrimaryExternalFldValues()
      const questionnaireData = getQuestionnaireData(extFields, externalFldValues, 2)
      return questionnaireCheck(questionnaireData).then(
        ({ passed }) => {
          if (passed) {
            return onSubmit()
          } else {
            setStatus(STATUSES.QUESTIONNAIRE_FAIL)
            return Promise.reject(new Error('questionnaire fail'))
          }
        },
        (error) => {
          setSendError(error)
          return Promise.reject(error)
        })
    } else {
      return onSubmit()
    }
  }

  const onSubmit = () => {
    const formData = getFormData()
    const resolve = (data) => {
      if (isJointAccount && (isNil(user.jointAccounts) || !isEmpty(user.jointAccounts))) {
        setIsLoading(false)
      } else {
        return verifyAccounts().then(() => {
          setSent(true)
          setIsLoading(false)
          pushKYC()
        }, reject)
      }
      setSendError({})
      pushKYC()
      return data
    }
    /**
     * @param {ErrorCommon} error
     */
    const reject = (error) => {
      setSent(false)
      setSendError(error)
      return Promise.reject(new Error('addAccount fail'))
    }
    return addAccount(formData).then(resolve, reject)
  }

  const onSubmitJointAcc = ({ isLastJointAcc = false }) => {
    const formData = getFormData(true)
    const resolve = (data) => {
      if (isLastJointAcc) {
        verifyAccounts().then(() => {
          setSent(true)
          setIsLoading(false)
          pushKYC()
        }, reject)
      } else {
        setIsLoading(false)
      }
      setSendError({})
      pushKYC()
      return data
    }
    /**
     * @param {ErrorCommon} error
     */
    const reject = (error) => {
      setSent(false)
      setSendError(error)
      setStatus(STATUSES.JOINT_ADD_ACCOUNT_ERROR)
      return Promise.reject(new Error('addAccount joint fail'))
    }
    return addAccount(formData).then(resolve, reject)
  }

  const onQuestionnaireContinue = () => {
    onSubmit().then(() => {
      if (isJointAccount && (isNil(user.jointAccounts) || !isEmpty(user.jointAccounts))) {
        setStatus('')
        dispatch({ type: 'setAccId', accId: accId + 1 })
      }
    }, () => {
      setStatus('')
    })
  }

  const onQuestionnaireTryLater = () => {
    const { idType } = form?.[accId]
    const externalFieldsValues = { idType, ...additionalFldValues(form[accId])(fldsDescriptionList) }
    tryLater(externalFieldsValues).then(({ result }) => {
      setStatus(STATUSES.QUESTIONNAIRE_TRY_LATER)
    }, error => {
      setSendError(error)
      setStatus('')
    })
  }

  if (sent) {
    const shouldRedirect = compose(
      find(propEq('name', 'shouldRedirectIn10Sec')),
      filterFlagFields
    )(fldsDescriptionList)
    return <SuccessMessage shouldRedirect={shouldRedirect} />
  }

  if (equals(user.scanStatus, 'DONE')) {
    return <VerifiedMessage />
  }

  if (status === STATUSES.QUESTIONNAIRE_TRY_LATER) {
    return <QuestionnaireTryLaterMessage />
  }

  return (
    <>
      {!isEmpty(sendError) && (
        <RetryError error={sendError} handleTryAgain={handleTryAgain} />
      )}
      <Page
        caption={t('verifyForm.title')}
        text={tier2FormCaption}
        className={!isEmpty(sendError) || status === STATUSES.QUESTIONNAIRE_FAIL ? styles.hide : ''}
      >
        <TierForm
          isLoadingSubmit={isLoading}
          onSubmit={onSubmitWithQuestionnaire}
          buttonName={t('verifyForm.verify')}
          onSubmitJoint={onSubmitJointAcc}
        />
      </Page>
      {status === STATUSES.QUESTIONNAIRE_FAIL && (
        <QuestionnaireFailMessage
          onContinue={onQuestionnaireContinue}
          onTryLater={onQuestionnaireTryLater}
        />)}
    </>)
}

export { Tier2Register }
